import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ApproveConfirmDialog } from '@menu-items/approve-confirm-dialog/approve-confirm-dialog';
import { EstablishmentOption, MenuItemInventory } from '@menu-items/menu-item-inventory/menu-item-inventory';
import { RejectReasonDialog } from '@menu-items/reject-reason-dialog/reject-reason-dialog';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { PanelModule } from 'primeng/panel';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { firstValueFrom } from 'rxjs';

import { PageHeader } from '@core/components/page-header/page-header';
import { ImageFallbackDirective } from '@core/directives/image-fallback';
import { SubmitDecisionRequest } from '@core/models/approval-decision';
import {
  DailyInventoryV1,
  EntreeNutrientContentV1,
  EntreeScoreV1,
  EntreeSizeV1,
  EntreeV1,
  EstablishmentPricingV1,
  MenuItemStatus,
  NutrientTargetSectionV1,
  NutrientTargetsV1,
  PhenotypeV1,
  ScoringCategoryV1
} from '@core/models/menu-item-pending';
import { ProducerV1 } from '@core/models/producer-admin';
import { MenuItemsService } from '@core/services/menu-items';
import { ProducersService } from '@core/services/producers';
import { scoreBandModifier } from '@core/utils/score-band';

const FROM_PRODUCER_PREFIX = 'producer/';

const SCHEDULE_AND_SAVE_NO_STOCK_WARNING =
  'Schedule Ahead & Save has no stock: every max daily amount is 0 or unset. Approving publishes this item, but ' +
  'it will not be offered on Schedule Ahead & Save on any date until stock is set.';

@Component({
  selector: 'app-menu-item-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    CardModule,
    MessageModule,
    PanelModule,
    SkeletonModule,
    TagModule,
    PageHeader,
    ImageFallbackDirective,
    ApproveConfirmDialog,
    RejectReasonDialog,
    MenuItemInventory
  ],
  templateUrl: './menu-item-detail.html',
  styleUrl: './menu-item-detail.scss'
})
export class MenuItemDetailPage {
  private readonly menuItems = inject(MenuItemsService);
  private readonly producers = inject(ProducersService);
  private readonly router = inject(Router);
  private readonly messages = inject(MessageService);

  readonly entreeId = input.required<string>();
  readonly from = input<string | undefined>(undefined);

  readonly entree = signal<EntreeV1 | null>(null);
  readonly loading = signal<boolean>(false);
  readonly error = signal<boolean>(false);
  readonly submitting = signal<boolean>(false);
  readonly advancing = signal<boolean>(false);
  readonly approveDialogVisible = signal<boolean>(false);
  readonly rejectDialogVisible = signal<boolean>(false);
  private readonly fetchedProducer = signal<ProducerV1 | null>(null);
  readonly targetPhenotypeId = signal<string | null>(null);
  readonly phenotypes = signal<PhenotypeV1[]>([]);
  readonly scores = signal<EntreeScoreV1[]>([]);
  readonly pricing = signal<EstablishmentPricingV1[]>([]);
  readonly fulfillmentModes = computed(() => {
    const groups = this.pricing();
    const modes: string[] = [];
    if (groups.some((group) => group.onDemand)) {
      modes.push('On-Demand');
    }
    if (groups.some((group) => group.scheduleAndSave)) {
      modes.push('Schedule Ahead & Save');
    }
    return modes;
  });
  // Every Schedule Ahead & Save group, including the ones with nothing set. Hiding the row when there was no
  // stock hid the one fact that decides whether a member can ever order the item (MLP-1266).
  readonly scheduleAndSaveInventory = computed(() => this.pricing().filter((group) => group.scheduleAndSave));

  /**
   * Establishments that offer this entree, one per pricing group, for the inventory picker. Named from
   * the fetched producer where possible; a group whose establishment is not on the producer payload
   * falls back to a truncated id, the same way the pricing and weekly-inventory rows label it.
   */
  readonly offeringEstablishments = computed<EstablishmentOption[]>(() => {
    const namesById = new Map((this.fetchedProducer()?.establishments ?? []).map((e) => [e.id.toLowerCase(), e.name]));
    return this.pricing().map((group) => ({
      id: group.establishmentGuid,
      name: namesById.get(group.establishmentGuid.toLowerCase()) ?? group.establishmentGuid.slice(0, 8)
    }));
  });

  readonly scheduleAndSaveWithoutStock = computed(() =>
    this.pricing().filter(
      (group) => group.scheduleAndSave && !(group.maxDailyAmounts ?? []).some((amount) => amount.maximum > 0)
    )
  );

  readonly missingScheduleAndSaveStock = computed(() => this.scheduleAndSaveWithoutStock().length > 0);

  readonly approveWarning = computed(() =>
    this.missingScheduleAndSaveStock() ? SCHEDULE_AND_SAVE_NO_STOCK_WARNING : null
  );
  readonly scoringCategories = signal<ScoringCategoryV1[]>([]);
  readonly nutritionBySize = signal<Record<string, EntreeNutrientContentV1[]>>({});
  private readonly expandedTargets = signal<ReadonlySet<string>>(new Set<string>());

  readonly fromProducerId = computed<string | null>(() => {
    const value = this.from();
    if (!value || !value.startsWith(FROM_PRODUCER_PREFIX)) return null;
    const producerId = value.slice(FROM_PRODUCER_PREFIX.length);
    return producerId || null;
  });

  readonly entreeName = computed(() => this.entree()?.name ?? '');
  readonly producerName = computed(() => {
    const e = this.entree();
    if (!e) return '';
    return this.fetchedProducer()?.name ?? this.producers.producerNameById()[e.producerId] ?? '';
  });
  readonly backCommands = computed<string[]>(() => {
    const producerId = this.fromProducerId();
    return producerId ? ['/producers', producerId] : ['/menu-items'];
  });
  readonly backQueryParams = computed<Record<string, string> | null>(() => {
    return this.fromProducerId() ? { tab: 'menu-items' } : null;
  });

  /**
   * Phenotype used to display scores. Prefers the producer's target market; falls back to the
   * first scored phenotype when the target is unknown (drafts submitted before retention, or
   * deleted on approval) so the review screen still shows score data.
   */
  readonly effectivePhenotypeId = computed<string | null>(() => {
    const target = this.targetPhenotypeId();
    if (target) return target;
    return this.scores()[0]?.phenotypeId ?? null;
  });
  readonly usingFallbackPhenotype = computed<boolean>(
    () => !this.targetPhenotypeId() && this.effectivePhenotypeId() !== null
  );
  readonly targetMarketName = computed<string>(() => {
    const id = this.effectivePhenotypeId();
    if (!id) return '';
    return this.phenotypes().find((p) => p.phenotypeId === id)?.phenotypeName ?? '';
  });
  private readonly categoryNameById = computed<Record<number, string>>(() =>
    Object.fromEntries(this.scoringCategories().map((c) => [c.scoringCategoryId, c.displayName ?? '']))
  );

  constructor() {
    effect(() => {
      this.entreeId();
      untracked(() => void this.loadDetail());
    });
  }

  private navigateBack(): void {
    const producerId = this.fromProducerId();
    if (producerId) {
      this.router.navigate(['/producers', producerId], { queryParams: { tab: 'menu-items' } });
      return;
    }
    this.router.navigate(['/menu-items']);
  }

  /** Jump to the oldest other pending item; used by the Next button and after queue decisions. */
  async goToNextPending(): Promise<void> {
    this.advancing.set(true);
    try {
      const response = await firstValueFrom(
        this.menuItems.getPendingApprovalList({ pageNum: 1, pageSize: 2, sort: 'submittedAt:asc' })
      );
      const next = (response.data ?? []).find((item) => item.id !== this.entreeId());
      if (next) {
        this.router.navigate(['/menu-items', next.id]);
        return;
      }
      this.messages.add({
        severity: 'success',
        summary: 'Queue clear',
        detail: 'No more menu items pending approval.'
      });
      this.router.navigate(['/menu-items']);
    } catch {
      this.router.navigate(['/menu-items']);
    } finally {
      this.advancing.set(false);
    }
  }

  async loadDetail(): Promise<void> {
    this.loading.set(true);
    this.error.set(false);
    this.fetchedProducer.set(null);

    try {
      const detail = await firstValueFrom(this.menuItems.getEntreeDetail(this.entreeId()));
      this.entree.set(detail);
      void this.hydrateProducerName(detail.producerId);
      void this.loadReviewData(detail);
    } catch {
      this.error.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  private async hydrateProducerName(producerId: string): Promise<void> {
    try {
      const producer = await firstValueFrom(this.producers.getProducerDetail(producerId));
      this.fetchedProducer.set(producer);
    } catch {
      // leave blank; the template falls back to the producer id
    }
  }

  private async loadReviewData(detail: EntreeV1): Promise<void> {
    this.targetPhenotypeId.set(null);
    this.phenotypes.set([]);
    this.scores.set([]);
    this.scoringCategories.set([]);
    this.nutritionBySize.set({});
    this.pricing.set([]);

    await Promise.allSettled([
      this.loadTargetMarket(detail.id),
      this.loadScores(detail.id),
      this.loadPhenotypes(),
      this.loadScoringCategories(),
      this.loadNutrition(detail.sizes),
      this.loadPricing(detail.id)
    ]);
  }

  private async loadTargetMarket(entreeId: string): Promise<void> {
    const target = await firstValueFrom(this.menuItems.getEntreeTargetMarket(entreeId));
    this.targetPhenotypeId.set(target.phenotypeId);
  }

  private async loadScores(entreeId: string): Promise<void> {
    this.scores.set(await firstValueFrom(this.menuItems.getEntreeScores(entreeId)));
  }

  formatInventory(amounts: DailyInventoryV1[]): string {
    if (amounts.length === 0) {
      return 'Not set';
    }
    const maxima = amounts.map((amount) => amount.maximum);
    if (maxima.every((maximum) => maximum === maxima[0])) {
      return `${maxima[0]} per day`;
    }
    return amounts.map((amount) => `${amount.dayOfWeek.slice(0, 3)} ${amount.maximum}`).join(' \u00b7 ');
  }

  private async loadPricing(entreeId: string): Promise<void> {
    this.pricing.set(await firstValueFrom(this.menuItems.getEntreePricing(entreeId)));
  }

  private async loadPhenotypes(): Promise<void> {
    this.phenotypes.set(await firstValueFrom(this.menuItems.getPhenotypes()));
  }

  private async loadScoringCategories(): Promise<void> {
    this.scoringCategories.set(await firstValueFrom(this.menuItems.getScoringCategories()));
  }

  private async loadNutrition(sizes: EntreeSizeV1[]): Promise<void> {
    const entries = await Promise.all(
      sizes.map(async (size) => {
        try {
          const nutrition = await firstValueFrom(this.menuItems.getSizeNutrition(size.entreeSizeId));
          return [size.entreeSizeId, nutrition] as const;
        } catch {
          return [size.entreeSizeId, [] as EntreeNutrientContentV1[]] as const;
        }
      })
    );
    this.nutritionBySize.set(Object.fromEntries(entries));
  }

  scoreForSize(entreeSizeId: string): EntreeScoreV1 | undefined {
    const phenotypeId = this.effectivePhenotypeId();
    if (!phenotypeId) return undefined;
    return this.scores().find((s) => s.entreeSizeId === entreeSizeId && s.phenotypeId === phenotypeId);
  }

  categoryName(nutrientCategoryId: number): string {
    return this.categoryNameById()[nutrientCategoryId] ?? `Category ${nutrientCategoryId}`;
  }

  nutritionForSize(entreeSizeId: string): EntreeNutrientContentV1[] {
    return this.nutritionBySize()[entreeSizeId] ?? [];
  }

  nutrientTargetsForSize(entreeSizeId: string): NutrientTargetsV1 {
    const sizeScore = this.scoreForSize(entreeSizeId);
    const categoryScoreById = new Map<number, number>(
      (sizeScore?.categoryScores ?? []).map((category) => [category.nutrientCategoryId, category.score])
    );
    const nutrientsByCategory = new Map<number, EntreeNutrientContentV1[]>();
    for (const nutrient of this.nutritionForSize(entreeSizeId)) {
      const list = nutrientsByCategory.get(nutrient.scoringCategoryId) ?? [];
      list.push(nutrient);
      nutrientsByCategory.set(nutrient.scoringCategoryId, list);
    }

    const sectionsByHeader = new Map<string, NutrientTargetSectionV1>();
    const order: string[] = [];
    for (const category of this.scoringCategories()) {
      const key = category.groupName ?? '';
      let section = sectionsByHeader.get(key);
      if (!section) {
        section = { header: category.groupName, categories: [] };
        sectionsByHeader.set(key, section);
        order.push(key);
      }
      section.categories.push({
        scoringCategoryId: category.scoringCategoryId,
        displayName: category.displayName ?? this.categoryName(category.scoringCategoryId),
        score: categoryScoreById.get(category.scoringCategoryId) ?? null,
        nutrients: nutrientsByCategory.get(category.scoringCategoryId) ?? []
      });
    }

    return { overallScore: sizeScore?.score ?? null, sections: order.map((key) => sectionsByHeader.get(key)!) };
  }

  formatScore(score: number | null): string {
    // Scores arrive on a 0-1 scale (chef-tool draft scores and NE phenotype scores alike).
    return score === null ? '—' : `${Math.round(score * 1000) / 10}%`;
  }

  scoreBadgeClass(score: number | null): string {
    return `target-badge--${scoreBandModifier(score)}`;
  }

  targetKey(entreeSizeId: string, scoringCategoryId: number): string {
    return `${entreeSizeId}:${scoringCategoryId}`;
  }

  isTargetExpanded(entreeSizeId: string, scoringCategoryId: number): boolean {
    return this.expandedTargets().has(this.targetKey(entreeSizeId, scoringCategoryId));
  }

  toggleTarget(entreeSizeId: string, scoringCategoryId: number): void {
    const key = this.targetKey(entreeSizeId, scoringCategoryId);
    this.expandedTargets.update((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  openApprove(): void {
    this.approveDialogVisible.set(true);
  }

  openReject(): void {
    this.rejectDialogVisible.set(true);
  }

  onApprove(): void {
    const detail = this.entree();
    if (!detail) return;

    const payload: SubmitDecisionRequest = {
      entreeId: detail.id,
      decision: 'Approved'
    };

    void this.submitDecision(payload, 'Menu item approved');
  }

  onReject(payload: { reasonCode: string; reasonDetail?: string }): void {
    const detail = this.entree();
    if (!detail) return;

    const decisionPayload: SubmitDecisionRequest = {
      entreeId: detail.id,
      decision: 'Rejected',
      reasonCode: payload.reasonCode,
      reasonDetail: payload.reasonDetail
    };

    void this.submitDecision(decisionPayload, 'Menu item rejected');
  }

  getStatusSeverity(status: MenuItemStatus): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (status) {
      case MenuItemStatus.Approved:
      case MenuItemStatus.Completed:
        return 'success';
      case MenuItemStatus.Rejected:
        return 'danger';
      case MenuItemStatus.PendingApproval:
        return 'warn';
      default:
        return 'info';
    }
  }

  private async submitDecision(payload: SubmitDecisionRequest, successMessage: string): Promise<void> {
    this.submitting.set(true);

    try {
      await firstValueFrom(this.menuItems.submitDecision(payload));
      this.approveDialogVisible.set(false);
      this.rejectDialogVisible.set(false);
      this.messages.add({ severity: 'success', summary: 'Decision submitted', detail: successMessage });
      if (this.fromProducerId()) {
        this.navigateBack();
      } else {
        await this.goToNextPending();
      }
    } catch (error) {
      const httpError = error as HttpErrorResponse;

      if (httpError?.status === 404) {
        this.approveDialogVisible.set(false);
        this.rejectDialogVisible.set(false);
        this.messages.add({
          severity: 'warn',
          summary: 'Already decided',
          detail: 'This menu item no longer has a pending approval — likely already decided. Refresh the queue.'
        });
        this.navigateBack();
        return;
      }

      if (httpError?.status === 409) {
        this.approveDialogVisible.set(false);
        this.rejectDialogVisible.set(false);
        this.messages.add({
          severity: 'warn',
          summary: 'Already decided',
          detail: 'This menu item was already decided by another reviewer or via Slack. Refresh the queue.'
        });
        this.navigateBack();
        return;
      }

      this.messages.add({
        severity: 'error',
        summary: 'Decision failed',
        detail: 'Could not submit the decision. Please try again.'
      });
    } finally {
      this.submitting.set(false);
    }
  }
}
